import React, { useEffect, useState } from 'react';
import { ResultComponentProps } from '../../common/types';
import { Table, Button, message, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import BaseResultDisplay from '../../common/BaseResultDisplay';

// 截断序列显示函数
const truncateSequence = (sequence: string, maxLength: number = 60) => {
  if (!sequence) return '';
  return sequence.length > maxLength ? sequence.substring(0, maxLength) + '...' : sequence;
};

// 分子量结果展示组件
const MolecularWeightResult: React.FC<ResultComponentProps> = ({ data, sequence, title, onDeleteResult, multiResults, onExportAllCSV }) => {
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    try {
      if (data && data.results && Array.isArray(data.results)) {
        let sourceResults = data.results;

        if (data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)) {
          sourceResults = data.results[0].result.results;
        }

        const formattedData = sourceResults.map((item: any, index: number) => ({
          key: String(index + 1),
          id: item.sequence_id || item.id || `序列${index + 1}`,
          sequence: item.sequence,
          length: item.result?.sequence_length || item.sequence_length || item.sequence?.length || 0,
          molecularWeight: item.result?.molecular_weight || item.molecular_weight || 0,
          averageResidueMass:
            item.result?.molecular_weight && (item.result?.sequence_length || item.sequence_length)
              ? (item.result.molecular_weight / (item.result.sequence_length || item.sequence_length))
              : item.averageResidueMass || 0,
          success: item.success !== false,
          error: item.error || null
        }));
        setTableData(formattedData);
      } else if (Array.isArray(data)) {
        const formattedData = data.map((item: any, index: number) => ({
          key: String(index + 1),
          id: item.sequence_id || item.id || `序列${index + 1}`,
          sequence: item.sequence,
          length: item.sequence_length || item.result?.sequence_length || item.sequence?.length || 0,
          molecularWeight: item.molecular_weight || item.result?.molecular_weight || 0,
          averageResidueMass: item.averageResidueMass || (item.molecular_weight && item.sequence_length
            ? item.molecular_weight / item.sequence_length
            : 0),
          success: item.success !== false,
          error: item.error || null
        }));
        setTableData(formattedData);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error('处理数据时出错:', error);
      message.error('处理数据时出错');
      setTableData([]);
    }
  }, [data]);

  // 导出为CSV
  const handleExportCSV = () => {
    try {
      // 表头
      const headers = [
        'ID',
        '序列',
        '长度',
        '分子量(Da)',
        '平均残基质量(Da/残基)'
      ].join(',');

      // 行数据 - CSV中包含完整序列
      const rows = tableData.map(item => [
        `"${item.id || ''}"`,
        `"${item.sequence || ''}"`, // 完整序列
        item.length || 0,
        (item.molecularWeight || 0).toFixed(2),
        (item.averageResidueMass || 0).toFixed(2)
      ].join(','));

      // 创建CSV内容
      const csvContent = `${headers}\n${rows.join('\n')}`;

      // 创建并下载CSV文件
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `分子量分析结果_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('CSV文件导出成功');
    } catch (error) {
      console.error('导出CSV文件失败:', error);
      message.error('导出CSV文件失败');
    }
  };

  // 创建表格列定义
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
    { 
      title: '序列', 
      dataIndex: 'sequence', 
      key: 'sequence', 
      width: 200,
      render: (sequence: string) => (
        <Tooltip title={sequence} placement="topLeft">
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {truncateSequence(sequence)}
          </span>
        </Tooltip>
      )
    },
    { title: '长度', dataIndex: 'length', key: 'length' },
    { 
      title: '分子量', 
      dataIndex: 'molecularWeight', 
      key: 'molecularWeight', 
      render: (value: number) => `${value.toFixed(2)} Da` 
    },
    { 
      title: '平均残基质量', 
      dataIndex: 'averageResidueMass', 
      key: 'averageResidueMass', 
      render: (value: number) => `${value.toFixed(2)} Da/残基` 
    },
  ];

  return (
    <BaseResultDisplay
      data={data}
      title={title || "分子量分析结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleExportCSV}
        >
          导出CSV
        </Button>
        {onExportAllCSV && (
          <Button 
            icon={<DownloadOutlined />}
            onClick={onExportAllCSV}
          >
            导出所有结果
          </Button>
        )}
      </div>

      <Table 
        columns={columns} 
        dataSource={tableData} 
        pagination={tableData.length > 10 ? { pageSize: 10 } : false} 
        bordered 
        size="middle"
        scroll={{ x: 'max-content' }}
      />
    </BaseResultDisplay>
  );
};

export default MolecularWeightResult;